'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useT } from '@/i18n/client';
import { getConsent, setConsent } from '@/lib/consent';

/**
 * GDPR-style cookie banner + preference centre. Analytics stay OFF until the
 * visitor opts in — either via "Accept all" or by enabling the Analytics
 * category in Cookies Settings. Necessary cookies (language, sign-in) are
 * always on. Rendered after mount so the server HTML never flashes it.
 */
/** Event any element can dispatch to re-open the preference centre later
 *  (e.g. the footer "Cookie settings" link): `window.dispatchEvent(new
 *  Event(OPEN_COOKIE_SETTINGS))`. */
export const OPEN_COOKIE_SETTINGS = 'vn:open-cookie-settings';

export function CookieConsent() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    setVisible(getConsent() === null);
    function openSettings() {
      // Reflect the visitor's current choice when re-opening later.
      setAnalytics(getConsent() === 'all');
      setSettingsOpen(true);
    }
    window.addEventListener(OPEN_COOKIE_SETTINGS, openSettings);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, openSettings);
  }, []);

  // Nothing to show unless the first-run banner is up or the centre was reopened.
  if (!visible && !settingsOpen) return null;

  function acceptAll() {
    setConsent('all');
    setVisible(false);
    setSettingsOpen(false);
  }
  function saveChoices() {
    setConsent(analytics ? 'all' : 'essential');
    setVisible(false);
    setSettingsOpen(false);
  }

  return (
    <>
      {visible ? (
      <div className="vn-consent" role="region" aria-label={t('consent.title')}>
        <div className="vn-consent__inner">
          <p className="vn-consent__copy">
            {t('consent.copy')}{' '}
            <Link href="/privacy" className="vn-consent__link">
              {t('consent.learnMore')}
            </Link>
          </p>
          <div className="vn-consent__actions">
            <button type="button" className="vn-consent__btn vn-consent__btn--fill" onClick={acceptAll}>
              {t('consent.acceptAll')}
            </button>
            <button
              type="button"
              className="vn-consent__btn vn-consent__btn--outline"
              onClick={() => setSettingsOpen(true)}
            >
              {t('consent.settings')}
            </button>
          </div>
        </div>
      </div>
      ) : null}

      {settingsOpen ? (
        <>
          <div className="vn-consent-backdrop" onClick={() => setSettingsOpen(false)} />
          <div className="vn-consent-prefs" role="dialog" aria-modal="true" aria-label={t('consent.prefsTitle')}>
            <h2 className="vn-consent-prefs__title">{t('consent.prefsTitle')}</h2>
            <p className="vn-consent-prefs__intro">{t('consent.prefsIntro')}</p>

            <div className="vn-consent-cat">
              <div className="vn-consent-cat__head">
                <span className="vn-consent-cat__name">{t('consent.necessary')}</span>
                <span className="vn-consent-cat__badge">{t('consent.alwaysOn')}</span>
              </div>
              <p className="vn-consent-cat__desc">{t('consent.necessaryDesc')}</p>
            </div>

            <div className="vn-consent-cat">
              <div className="vn-consent-cat__head">
                <span className="vn-consent-cat__name">{t('consent.analytics')}</span>
                <label className="vn-switch">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    aria-label={t('consent.analytics')}
                  />
                  <span className="vn-switch__track" aria-hidden="true">
                    <span className="vn-switch__thumb" />
                  </span>
                </label>
              </div>
              <p className="vn-consent-cat__desc">{t('consent.analyticsDesc')}</p>
            </div>

            <div className="vn-consent-prefs__actions">
              <button type="button" className="vn-consent__btn vn-consent__btn--fill" onClick={saveChoices}>
                {t('consent.confirm')}
              </button>
              <button type="button" className="vn-consent__btn vn-consent__btn--outline" onClick={acceptAll}>
                {t('consent.acceptAll')}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
